/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Component, forwardRef, HostListener, Input } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
    selector: 'app-lb-reveal-password',
    templateUrl: './revealPassword.component.html',
    styleUrls: ['./revealPassword.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => RevealPasswordComponent),
        }
    ]
})

export class RevealPasswordComponent implements ControlValueAccessor {
    /**
     * Form group
     */
    @Input() public formGroup: FormGroup = new FormGroup({
        default: new FormControl('')
    });

    /**
     * Field id
     */
    @Input() public id: string;

    /**
     * Form control name
     */
    @Input() public formControlName: string = 'default';

    /**
     * Title for the password field
     */
    @Input() public title: string = 'Password';

    /**
     * Class name
     */
    @Input() public className: string;

    /**
     * Toggle visibility of the password text.
     */
    public inputType: 'password' | 'text' = 'password';

    /**
     * Store the icon visible state name
     */
    public iconStateName: string = 'invisible';

    /**
     * Set mousedown target id.
     * @param event
     */
    @HostListener('document:keydown', ['$event'])
    public onKeyPress(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            event.preventDefault();
        }
    }

    /**
     * Event handler for when the view changes
     * // Note: Required by implementation. Do not remove.
     */
    public registerOnChange(): void {
    }

    /**
     * Register a function to determine if the input has been touched
     * // Note: Required by implementation. Do not remove.
     */
    public registerOnTouched(): void {
    }

    /**
     * Write value to input
     * // Note: Required by implementation. Do not remove.
     */
    public writeValue(): void {
    }

    /**
     * Toggle the password between plain and dotted text.
     */
    public togglePasswordView(): void {
        if (this.inputType === 'password') {
            this.inputType = 'text';
            this.iconStateName = 'visible';
        } else if (this.inputType === 'text') {
            this.inputType = 'password';
            this.iconStateName = 'invisible';
        }
    }

    /**
     * Disable the reveal password button if the form is disabled.
     */
    public revealPasswordButtonIsDisabled(): boolean {
        return this.formGroup.get(this.formControlName).disabled;
    }
}
